import { z } from 'zod';

/**
 * Charge-session map contract v2 — server-driven viewport clustering
 * (BIZOPS-1588 ↔ 1589 ↔ 1591).
 *
 * Pinned spec: `.context/charge-sessions-spec.md` §15 (postMessage contract v2).
 * This SPA cannot import `@repo/*`, so the shared schemas are redefined locally.
 * They MUST stay byte-for-byte compatible with the Hub studio-viewer side that
 * posts/receives these messages and with the gateway Tier-1 response (1591).
 *
 * Rework from v1: the all-points Tier-1 payload is replaced by server-side H3
 * viewport clustering. The gateway now bins active sessions into H3 cells per
 * viewport and returns:
 *   - `clusters`: aggregated H3 cells (count bubbles) shown at low/medium zoom;
 *   - `leaves`:   individual location/EVSE points returned at/above the leaf
 *                 zoom threshold (z >= 16).
 * The SPA drives this by sending its current viewport (`monta:viewport`) on
 * every map move/zoom; the parent re-queries the gateway and pushes a fresh
 * `monta:charge-sessions` down.
 *
 * - cluster ids are H3 hex strings → `z.string()`.
 * - leaf ids are numeric-as-string on the wire → `z.coerce.string()`.
 * - `powerKw` is omitted in v1 (no instantaneous-power source) but kept
 *   `.nullish()` for forward-compat.
 * - `consumerLabel` is PII; the public lane forces it to `null`.
 * - `state` carries a charge-state string (charging, paused, …) mapped to i18n
 *   labels in the panel; unknown values render raw.
 */

/** Geo granularity of a leaf point / session bucket. */
export const sessionLevelSchema = z.enum(['location', 'evse']);
export type SessionLevel = z.infer<typeof sessionLevelSchema>;

/** A single active charge session (Tier-2 detail). */
export const sessionSchema = z.object({
  sessionId: z.coerce.string(),
  evseName: z.string(),
  chargePointName: z.string(),
  state: z.string(),
  kwh: z.number(),
  // Omitted in v1; kept nullish for forward-compat.
  powerKw: z.number().nullish(),
  startedAt: z.string(),
  // PII — null on the public lane.
  consumerLabel: z.string().nullish(),
  costLabel: z.string().nullish(),
  currency: z.string().nullish(),
});
export type Session = z.infer<typeof sessionSchema>;

/**
 * A server-side H3 cluster: an aggregated bin of active points for the current
 * viewport/zoom. `id` is the H3 hex cell string; clicking it zooms the map in,
 * which triggers a fresh viewport request and finer clusters/leaves.
 */
export const sessionClusterSchema = z.object({
  // H3 hex cell id — already a string, not numeric.
  id: z.string(),
  lat: z.number(),
  lng: z.number(),
  // Distinct active points (locations/EVSEs) inside the cell.
  pointCount: z.number(),
  // Total active sessions across those points (drives the bubble emphasis).
  activeSessionCount: z.number(),
});
export type SessionCluster = z.infer<typeof sessionClusterSchema>;

/**
 * A leaf point: an individual location or EVSE with its active-session count,
 * returned once the viewport is zoomed past the leaf threshold (z >= 16).
 * Clicking a leaf opens the Tier-2 detail panel.
 */
export const sessionLeafSchema = z.object({
  id: z.coerce.string(),
  level: sessionLevelSchema,
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  activeSessionCount: z.number(),
});
export type SessionLeaf = z.infer<typeof sessionLeafSchema>;

/**
 * DOWN (parent → iframe) message: Tier-1 viewport clusters + leaves. Mirrors the
 * gateway Tier-1 response (1591): `{ generatedAt, zoom, resolution, clusters,
 * leaves }`. At low/medium zoom `leaves` is empty and `clusters` carries the
 * data; at/above the leaf threshold the inverse holds — and `resolution` is
 * `null` at leaf zoom (no cluster resolution once individual leaves are emitted).
 */
export const chargeSessionsMessageSchema = z.object({
  type: z.literal('monta:charge-sessions'),
  generatedAt: z.string(),
  // The zoom the server clustered for (echoes the requested viewport zoom).
  zoom: z.number(),
  // The H3 resolution the server binned at for this zoom. The gateway returns
  // `null` at leaf zoom (z >= 16) — there is no cluster resolution once it emits
  // individual leaves — so this is `.nullish()` (accept null/absent), not a bare
  // number. Sending a number here at leaf zoom is what the `?mock=1` dev mock
  // used to do, which masked this against the real gateway (1591) and dropped
  // every leaf on a failed parse.
  resolution: z.number().nullish(),
  clusters: z.array(sessionClusterSchema),
  leaves: z.array(sessionLeafSchema),
});
export type ChargeSessionsMessage = z.infer<typeof chargeSessionsMessageSchema>;

/**
 * DOWN (parent → iframe) message: Tier-2 detail for one leaf.
 */
export const sessionDetailMessageSchema = z.object({
  type: z.literal('monta:session-detail'),
  id: z.coerce.string(),
  level: sessionLevelSchema,
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  sessions: z.array(sessionSchema),
});
export type SessionDetailMessage = z.infer<typeof sessionDetailMessageSchema>;

/**
 * All inbound (DOWN) messages, discriminated on `type`. Use `.safeParse` so an
 * unrelated postMessage from the parent (or a malformed payload) is dropped
 * rather than throwing.
 */
export const inboundMessageSchema = z.discriminatedUnion('type', [
  chargeSessionsMessageSchema,
  sessionDetailMessageSchema,
]);
export type InboundMessage = z.infer<typeof inboundMessageSchema>;

/**
 * UP (iframe → parent) message: handshake posted on mount.
 */
export const readyMessageSchema = z.object({
  type: z.literal('monta:ready'),
});
export type ReadyMessage = z.infer<typeof readyMessageSchema>;

/**
 * UP (iframe → parent) message: the SPA's current map viewport. Posted
 * (debounced) on every map move/zoom-end and once after `monta:ready`. The
 * parent re-queries the gateway for this viewport and pushes a fresh
 * `monta:charge-sessions` down. Bounds mirror the gateway's
 * `top/bottom/left/right` (north/south/west/east) + `zoom`.
 */
export const viewportMessageSchema = z.object({
  type: z.literal('monta:viewport'),
  top: z.number(),
  bottom: z.number(),
  left: z.number(),
  right: z.number(),
  zoom: z.number(),
});
export type ViewportMessage = z.infer<typeof viewportMessageSchema>;

/** The viewport payload without the `type` discriminant (caller-facing arg). */
export type Viewport = Omit<ViewportMessage, 'type'>;

/**
 * UP (iframe → parent) message: request Tier-2 detail for a leaf. Sent on leaf
 * click only (clusters zoom instead).
 */
export const sessionDetailRequestMessageSchema = z.object({
  type: z.literal('monta:session-detail-request'),
  id: z.coerce.string(),
  level: sessionLevelSchema,
});
export type SessionDetailRequestMessage = z.infer<
  typeof sessionDetailRequestMessageSchema
>;

/** All outbound (UP) message shapes the SPA posts to the parent. */
export type OutboundMessage =
  | ReadyMessage
  | ViewportMessage
  | SessionDetailRequestMessage;
